/*
  FC Description — behaviour for the FC Description product block.
  Collapses the description to the configured line count and toggles it via
  the "Meer laden" / "Minder laden" button, with an eased height animation.
  The button only appears when the text overflows the clamp or the collapsed
  state suppresses media (re-checked on resize).
  Loaded by sections/main-product.liquid only when the block is present.
  Developer: Alfie
  Updated:   2026-08-20
*/

if (!customElements.get('fc-description')) {
  customElements.define('fc-description', class FcDescription extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.fc-description__content');
      this.toggle = this.querySelector('.fc-description__toggle');
      if (!this.content || !this.toggle) return;

      this.animation = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.toggle.addEventListener('click', this.onToggleClick.bind(this));
      this.collapse(false);

      // Line count at the clamp boundary shifts with viewport width; skip
      // while animating — the tween's own height changes would mis-measure
      new ResizeObserver(() => {
        if (!this.animation && !this.isExpanded()) this.updateToggleVisibility();
      }).observe(this.content);
    }

    isExpanded() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    collapse(animate) {
      const startHeight = this.content.clientHeight;

      this.content.classList.add('is-collapsed');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.textContent = this.toggle.dataset.labelMore;
      this.updateToggleVisibility();

      if (animate && !this.reducedMotion.matches) {
        const endHeight = this.content.clientHeight;
        // Animate unclamped so the tail text stays visible while shrinking;
        // the clamp (and its ellipsis) lands on finish
        this.content.classList.remove('is-collapsed');
        this.animateHeight(startHeight, endHeight, () => {
          this.content.classList.add('is-collapsed');
        });
      }
    }

    expand(animate) {
      const startHeight = this.content.clientHeight;

      this.content.classList.remove('is-collapsed');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.textContent = this.toggle.dataset.labelLess;
      this.toggle.hidden = false;

      if (animate && !this.reducedMotion.matches) {
        this.animateHeight(startHeight, this.content.scrollHeight);
      }
    }

    animateHeight(from, to, onFinish) {
      this.content.style.overflow = 'hidden';
      this.animation = this.content.animate(
        { height: [`${from}px`, `${to}px`] },
        { duration: 350, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
      this.animation.addEventListener('finish', () => {
        this.content.style.overflow = '';
        this.animation = null;
        if (onFinish) onFinish();
      });
    }

    updateToggleVisibility() {
      // Media/tables are display:none while collapsed (see fc-description.css),
      // so they don't count toward scrollHeight — without this check a short
      // text + image description would never get a toggle to reveal the image
      const hasSuppressedContent = this.content.querySelector('img, video, iframe, table') !== null;
      // +1 tolerates fractional line-height rounding
      const overflows = this.content.scrollHeight > this.content.clientHeight + 1;
      this.toggle.hidden = !overflows && !hasSuppressedContent;
    }

    onToggleClick() {
      if (this.animation) {
        this.animation.cancel();
        this.animation = null;
        this.content.style.overflow = '';
      }

      if (this.isExpanded()) {
        this.collapse(true);
        // Collapsing from far down the page can strand the viewport
        if (this.getBoundingClientRect().top < 0) {
          this.scrollIntoView({ block: 'nearest', behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
        }
      } else {
        this.expand(true);
      }
    }
  });
}
